import { useMutation } from '@tanstack/vue-query'
import { miaApiClient } from '@/api/clients'
import { AxiosError } from 'axios'
import { useCustomParams } from '@/composables/useCustomParams'

type TextToSpeechRequest = {
  text: string
  lang: string
}

export const useTextToSpeech = () => {
  const { language } = useCustomParams()

  const { mutateAsync: ttsMutateAsync } = useMutation({
    mutationFn: ({ text, lang }: TextToSpeechRequest) =>
      miaApiClient.post<Blob>(
        '/tts',
        {
          text,
          lang
        },
        {
          responseType: 'blob'
        }
      ),
    onError: async (error: AxiosError) => {
      const message = error instanceof Error ? error.message : String(error)
      // TODO: process message
    }
  })

  /**
   * Performs text-to-speech conversion for the given message.
   * Sends the text and current language to the TTS API endpoint,
   * receives the audio blob response, creates an audio element,
   * and returns it for playback.
   *
   * @param {string} message - The text message to convert to speech.
   * @returns {Promise<HTMLAudioElement>} A promise that resolves to an HTMLAudioElement
   *   which can be used to play the generated speech audio.
   */
  const doTts = async (message: string): Promise<HTMLAudioElement> => {
    const resp = await ttsMutateAsync({ text: message, lang: language })

    const audioUrl = URL.createObjectURL(resp.data)
    const audio = new Audio(audioUrl)
    return audio
  }

  return { doTts }
}
